/**
 * AlertsContext — single fetch + SSE subscription shared across all consumers.
 * Prevents duplicate /api/alerts calls when useAlerts() is called in multiple components.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ConsumerUnitContext } from './ConsumerUnitContext';
import { fetchAlerts, acknowledgeAlert, resolveAlert, dismissAlert } from '../services/alertService';
import { useEventStream } from '../hooks/useEventStream';

const USE_MOCK = process.env.REACT_APP_MOCK === 'true';

const AlertsContext = createContext(null);

export function AlertsProvider({ children }) {
  const { getToken } = useAuth();
  // Safe access — AlertsProvider mounts before ConsumerUnitProvider in the tree
  const consumerCtx = useContext(ConsumerUnitContext);
  const selectedUnitId = consumerCtx?.selectedUnitId || 'all';
  const { subscribe } = useEventStream();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (USE_MOCK) { setLoading(false); return; }
    try {
      const data = await fetchAlerts(selectedUnitId, getToken);
      if (mountedRef.current) setAlerts(data.alerts ?? []);
    } catch {
      // silent — alerts are non-critical
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [getToken, selectedUnitId]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  // SSE: alert.created — prepend new alert
  useEffect(() => {
    const unsub1 = subscribe('alert.created', (data) => {
      setAlerts((prev) => [{ ...data, status: 'active', why: null, impacted: [], actions: [] }, ...prev]);
    });
    const unsub2 = subscribe('alert.enriched', (data) => {
      setAlerts((prev) => prev.map((a) =>
        a.id === data.alertId || a.alertId === data.alertId
          ? { ...a, why: data.why, impacted: data.impacted, actions: data.actions, confidence: data.confidence, enrichedAt: new Date().toISOString() }
          : a
      ));
    });
    return () => { unsub1(); unsub2(); };
  }, [subscribe]);

  const acknowledge = useCallback(async (id) => {
    try {
      await acknowledgeAlert(id, getToken);
      setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, status: 'acknowledged' } : a));
    } catch { /* silent */ }
  }, [getToken]);

  const resolve = useCallback(async (id) => {
    try {
      await resolveAlert(id, getToken);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch { /* silent */ }
  }, [getToken]);

  const dismiss = useCallback(async (id) => {
    try {
      await dismissAlert(id, getToken);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch { /* silent */ }
  }, [getToken]);

  const activeCount = alerts.filter((a) => a.status === 'active').length;

  const value = { alerts, loading, activeCount, acknowledge, resolve, dismiss, refresh: load };

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
}

export function useAlerts() {
  const ctx = useContext(AlertsContext);
  if (!ctx) throw new Error('useAlerts must be used within AlertsProvider');
  return ctx;
}
